import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Award, Copy, Code, ExternalLink, ShieldCheck, ShieldOff, Building2, Trophy, GraduationCap, Sparkles,
} from "lucide-react";

const META = {
  verified:           { label: "Verified Institution",        icon: ShieldCheck, color: "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30" },
  certified:          { label: "Certified Institution",       icon: Award,       color: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
  top:                { label: "Top Institution",             icon: Trophy,      color: "bg-violet-500/10 text-violet-700 border-violet-500/30" },
  top_placement:      { label: "Top Placement Institution",   icon: Building2,   color: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
  student_excellence: { label: "Student Excellence",          icon: GraduationCap, color: "bg-rose-500/10 text-rose-700 border-rose-500/30" },
  founder:            { label: "Founder Award",               icon: Sparkles,    color: "bg-yellow-500/10 text-yellow-700 border-yellow-500/30" },
  innovation:         { label: "Innovation Award",            icon: Sparkles,    color: "bg-pink-500/10 text-pink-700 border-pink-500/30" },
  emerging:           { label: "Emerging Institution",        icon: Sparkles,    color: "bg-teal-500/10 text-teal-700 border-teal-500/30" },
  excellence:         { label: "Excellence Award",            icon: Sparkles,    color: "bg-orange-500/10 text-orange-700 border-orange-500/30" },
};
const STATUS_BADGE = {
  active:    "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30",
  expired:   "bg-orange-100 text-orange-700 border-orange-300",
  suspended: "bg-amber-100 text-amber-800 border-amber-300",
  revoked:   "bg-red-100 text-ec-error border-red-300",
  pending_review: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function MyAwardsPage() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/my-awards")
      .then((r) => setRows(r.data.rows))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load."));
  }, []);

  return (
    <WorkspaceLayout subtitle="Institution workspace" title="My Awards & Embed Badges">
      <p className="text-sm text-ec-ink-soft mb-6 max-w-2xl">
        Every recognition you've earned. Drop a live verification badge on your website by copying
        the snippet below — it always re-validates against EthicCred so visitors can trust it.
      </p>

      {err && <Card className="rounded-sm border-ec-error/30 bg-ec-error/5"><CardContent className="p-5 text-ec-error" data-testid="awards-err">{err}</CardContent></Card>}

      {rows.length === 0 && !err && (
        <Card className="rounded-sm border-ec-line">
          <CardContent className="p-10 text-center text-ec-ink-soft" data-testid="my-awards-empty">
            <ShieldOff className="w-10 h-10 mx-auto mb-3 opacity-30" />
            No awards issued to your institution yet. Recognition is auto-evaluated based on your
            Trust Score and platform activity. Keep growing — your first badge is on its way.
          </CardContent>
        </Card>
      )}

      <div className="space-y-5" data-testid="my-awards-list">
        {rows.map((a) => <AwardRow key={a.id} a={a} />)}
      </div>
    </WorkspaceLayout>
  );
}

function AwardRow({ a }) {
  const meta = META[a.award_type] || { label: a.award_label, icon: Award, color: "" };
  const Icon = meta.icon;
  const verifyUrl = `${window.location.origin}/verify/institution/${a.award_no}`;
  const [showCode, setShowCode] = useState(false);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Card className="rounded-sm border-ec-line" data-testid={`my-award-${a.id}`}>
      <CardContent className="p-0">
        <div className="grid lg:grid-cols-12 gap-0">
          {/* Left preview */}
          <div className={`lg:col-span-4 p-6 border-r border-ec-line flex flex-col items-center justify-center gap-4 ${meta.color}`} data-testid={`my-award-preview-${a.id}`}>
            <Icon className="w-12 h-12" />
            <div className="text-center">
              <div className="eyebrow opacity-80">{meta.label}</div>
              <div className="font-mono-plex text-[10px] opacity-70 mt-1">#{a.award_no}</div>
            </div>
            <Badge className={`rounded-sm border ${STATUS_BADGE[a.status] || ""}`}>{a.status?.replace("_", " ")}</Badge>
            <div className="bg-white p-2 border border-ec-line rounded-sm">
              <QRCodeSVG value={verifyUrl} size={96} level="H" />
            </div>
          </div>

          {/* Right details + embed */}
          <div className="lg:col-span-8 p-6 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display text-xl font-black">{meta.label}</h3>
                <div className="text-xs font-mono-plex text-ec-ink-soft mt-1">
                  Issued {(a.issued_at || "—").slice(0, 10)}
                  {a.expires_at ? ` · Expires ${a.expires_at.slice(0, 10)}` : " · Perpetual"}
                </div>
              </div>
              <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-xs text-ec-emerald hover:underline inline-flex items-center gap-1" data-testid={`my-award-public-${a.id}`}>
                Open public page <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <CopyRow label="Verification URL" value={verifyUrl} onCopy={() => copy(verifyUrl, "URL")} testid={`my-award-url-${a.id}`} />
            <CopyRow label="Award number"     value={a.award_no} onCopy={() => copy(a.award_no, "Award number")} testid={`my-award-no-${a.id}`} />

            <div>
              <button onClick={() => setShowCode((s) => !s)} className="flex items-center gap-2 text-sm text-ec-ink hover:text-ec-emerald" data-testid={`my-award-toggle-code-${a.id}`}>
                <Code className="w-4 h-4" /> {showCode ? "Hide" : "Show"} HTML embed snippet
              </button>
              {showCode && (
                <div className="mt-3" data-testid={`my-award-code-${a.id}`}>
                  <div className="border border-ec-line bg-ec-mute p-3 overflow-x-auto">
                    <pre className="text-[11px] font-mono-plex text-ec-ink whitespace-pre-wrap break-all">{a.embed_snippet}</pre>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copy(a.embed_snippet, "Embed snippet")} className="rounded-sm h-8 mt-2 gap-1.5" data-testid={`my-award-copy-code-${a.id}`}>
                    <Copy className="w-3 h-3" /> Copy HTML
                  </Button>
                  <p className="text-[11px] text-ec-ink-soft mt-2">
                    Paste this anywhere on your institution website. It always re-validates against EthicCred —
                    if the badge ever becomes expired/suspended/revoked, the live verification page reflects it instantly.
                  </p>
                </div>
              )}
            </div>

            {a.reasons?.length > 0 && (
              <div className="text-xs text-ec-ink-soft border-t border-ec-line pt-3">
                <div className="eyebrow mb-1">Why you earned this</div>
                <ul className="list-disc list-inside space-y-0.5">{a.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CopyRow({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2 border border-ec-line bg-ec-mute/40 rounded-sm px-3 py-2" data-testid={testid}>
      <div className="eyebrow text-ec-ink-soft text-[10px] w-32 shrink-0">{label}</div>
      <code className="flex-1 text-xs font-mono-plex text-ec-ink truncate">{value}</code>
      <button onClick={onCopy} className="p-1.5 rounded-sm border border-ec-line hover:bg-white" data-testid={`${testid}-copy`}>
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}
